/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import { EuiFlexGroup, EuiSplitPanel, useEuiTheme } from '@elastic/eui'

import { getUserUsecase } from '@modules/profile-lib'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import DeploymentCreationWizardLayout from '@modules/deployment-creation-wizard/DeploymentCreationWizardLayout'

import { getLinks } from '@/lib/deployments/links'
import { kibanaGettingStartedUrl } from '@/lib/serviceProviderDeepLinks'
import { isHidden } from '@/lib/stackDeployments/selectors/metadata'
import {
  getFirstSliderClusterFromGet,
  getVersion,
} from '@/lib/stackDeployments/selectors/fundamentals'
import {
  isAnyResourceChanging,
  isEveryResourceStarted,
  isEsStopping,
  isEsStopped,
} from '@/lib/stackDeployments/selectors/configurationChanges'
import {
  hasHealthyResourcePlan,
  hasFailedCreatePlan,
} from '@/lib/stackDeployments/selectors/configurationChangeHealth'
import { getDisplayName } from '@/lib/stackDeployments/selectors/display'
import useDeploymentCreationWizardSteps from '@/components/StackDeploymentEditor/CreateStackDeploymentRoute/hooks'
import useEbtDeploymentCreationWizard from '@/components/StackDeploymentEditor/CreateStackDeploymentRoute/use-ebt-deployment-creation-wizard'

import { useHeaderDefinition } from '../DeploymentHeader/headerDefinitions'

import DeploymentGettingStartedFooter from './DeploymentGettingStartedFooter'
import DeploymentGettingStartedDetails from './DeploymentGettingStartedDetails'
import DeploymentGettingStartedHeader from './DeploymentGettingStartedHeader'
import { useDeploymentGettingStartedTitleAndDescription } from './hooks'

import type { AllProps, GettingStartedPhase } from './types'

const DeploymentGettingStarted: React.FunctionComponent<AllProps> = ({
  stackDeployment,
  credentials,
  deploymentTemplate,
  instanceType,
  isAnyAdminConsole,
  profile,
}) => {
  const { reportDeploymentKibanaEbt } = useEbtDeploymentCreationWizard()

  const [_, flags] = useFlagsWhenLoaded()

  const {
    euiTheme: {
      size: { xxxxl: euiSizeXXXXL },
      breakpoint: { m: euiBreakpointM },
    },
  } = useEuiTheme()

  const [, , generateStepDetails] = useDeploymentCreationWizardSteps('getting-started')

  const getTitleAndDescription = useDeploymentGettingStartedTitleAndDescription()

  const deploymentDisplayName = stackDeployment
    ? getDisplayName({ deployment: stackDeployment })
    : undefined

  const version = stackDeployment ? getVersion({ deployment: stackDeployment }) : undefined

  const { breadcrumbs } = useHeaderDefinition({
    deploymentDisplayName,
    version,
  })

  const [credentialsSavedOrSkipped, setCredentialsSavedOrSkipped] = useState<boolean>(false)

  if (!stackDeployment) {
    return null // sanity
  }

  const isStopped = isEsStopped({ deployment: stackDeployment })
  const isStopping = isEsStopping({ deployment: stackDeployment })
  const hidden = isHidden({ deployment: stackDeployment })

  const { guidedOnboarding: showGuidedOnboardingPage, deeplinkToSecurity, deeplinkToSearch } = flags

  const linkInfo =
    instanceType === `elasticsearch`
      ? []
      : getLinks({ deployment: stackDeployment, show: instanceType })

  const showDeploymentCompletedMessage = isEveryResourceStarted({ deployment: stackDeployment })

  const changingPlan = isAnyResourceChanging({ deployment: stackDeployment })

  const planInProgress =
    (linkInfo.length > 0 && !linkInfo[0]?.available && changingPlan) || changingPlan

  const disabled = linkInfo.length > 0 && !linkInfo[0]?.available && !changingPlan

  const kibanaResource = getFirstSliderClusterFromGet({
    deployment: stackDeployment,
    sliderInstanceType: `kibana`,
  })

  const isKibanaUnhealthy =
    kibanaResource !== null && !hasHealthyResourcePlan({ resource: kibanaResource })

  const createFailed =
    (!planInProgress && hasFailedCreatePlan({ deployment: stackDeployment })) ||
    (!planInProgress && isKibanaUnhealthy)

  const discoveryUseCase = profile ? getUserUsecase(profile) : undefined

  const deepLink = kibanaGettingStartedUrl({
    resource: kibanaResource,
    showGuidedOnboardingPage,
    discoveryUseCase: discoveryUseCase || undefined,
    deeplinkToSecurity,
    deeplinkToSearch,
  })

  const paddingSize = euiSizeXXXXL

  const displayCredentials = credentials != null && !credentialsSavedOrSkipped && !createFailed

  const gettingStartedPhase = determineGettingStartedPhase(
    displayCredentials,
    createFailed,
    planInProgress,
  )

  // We don't need to show this for deployments that are deleted
  if (isStopping || isStopped || hidden) {
    return null
  }

  const solutionView = stackDeployment.settings?.solution_type

  const steps = generateStepDetails('getting-started', gettingStartedPhase === 'deployment-created')

  return (
    <DeploymentCreationWizardLayout
      {...getTitleAndDescription(gettingStartedPhase)}
      breadcrumbs={breadcrumbs}
      steps={steps}
    >
      <EuiFlexGroup direction='column'>
        <EuiSplitPanel.Outer
          grow={true}
          css={{
            maxWidth: euiBreakpointM,
            margin: 'auto',
            padding: paddingSize,
          }}
        >
          <EuiSplitPanel.Inner>
            <EuiFlexGroup direction='column' gutterSize='xl'>
              <DeploymentGettingStartedHeader
                solutionView={solutionView}
                planInProgress={planInProgress}
                createFailed={createFailed}
                stackDeployment={stackDeployment}
              />
              <DeploymentGettingStartedDetails
                deployment={stackDeployment}
                deploymentTemplate={deploymentTemplate}
                gettingStartedPhase={gettingStartedPhase}
                onCredentialsContinue={() => setCredentialsSavedOrSkipped(true)}
                isAnyAdminConsole={isAnyAdminConsole}
              />
            </EuiFlexGroup>
            <div style={{ width: '100vh' }} />
          </EuiSplitPanel.Inner>
          <EuiSplitPanel.Inner>
            <DeploymentGettingStartedFooter
              planInProgress={planInProgress}
              stackDeployment={stackDeployment}
              createFailed={createFailed}
              showDeploymentCompletedMessage={showDeploymentCompletedMessage}
              deepLink={deepLink}
              disabled={disabled}
              onContinue={() =>
                reportDeploymentKibanaEbt({
                  answer: true,
                  resource: 'stateful',
                })
              }
            />
          </EuiSplitPanel.Inner>
        </EuiSplitPanel.Outer>
      </EuiFlexGroup>
    </DeploymentCreationWizardLayout>
  )
}

function determineGettingStartedPhase(
  displayCredentials: boolean,
  createFailed: boolean,
  planInProgress: boolean,
): GettingStartedPhase {
  if (displayCredentials) {
    return 'save-creds'
  }

  if (createFailed) {
    return 'deployment-failed'
  }

  if (planInProgress) {
    return 'creating-deployment'
  }

  return 'deployment-created'
}

export default DeploymentGettingStarted
